import React from "react";
import { ShieldCheck, FileSearch, Eye, Building2, Scale, Check } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";

const KYC_FEATURES = [
  { icon: ShieldCheck, title: "Identity Verification", body: "Thorough identification process for all clients." },
  { icon: FileSearch, title: "Background Checks", body: "Comprehensive background verification procedures." },
  { icon: Eye, title: "Ongoing Monitoring", body: "Continuous compliance monitoring and updates." },
];

const COMPANY_REVIEW = [
  "Financial Background",
  "Governance Structures",
  "Funding Sources",
  "Regulatory Framework",
  "Operational Stability",
];

const PROPERTY_CHECKS = [
  "Verifying property titles",
  "Thorough legal assessment",
  "Tax assessment and review",
  "Examining ongoing or historical disputes",
  "Environmental and zoning compliance",
];

export default function DueDiligencePage() {
  return (
    <div data-testid="due-diligence-page" className="page-fade">
      <PageHero
        eyebrow="Compliance & Security"
        title={<>Due <span className="italic">Diligence.</span></>}
        lede="At EMIVIA LIMITED we are committed to the highest standards of compliance and transparency in all of our operations."
        image="https://images.pexels.com/photos/14523757/pexels-photo-14523757.jpeg"
        testId="due-diligence-hero"
      />

      {/* KYC */}
      <section className="bg-[#070B14] py-24 sm:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
              KYC & Compliance
            </div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Comprehensive KYC <br />
              <span className="italic">and Compliance.</span>
            </h2>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-7 max-w-2xl">
            <p className="text-white/70 text-base sm:text-lg leading-relaxed font-light">
              At EMIVIA LIMITED, we are committed to the highest standards of compliance and
              transparency. We maintain <span className="text-[#D4AF37]">Full KYC</span> for all
              of our clients, ensuring thorough identification and background verification before
              engaging in any financial or business transactions.
            </p>
            <p className="mt-6 text-white/70 text-base sm:text-lg leading-relaxed font-light">
              This process helps safeguard the security and integrity of our operations,
              minimising risks related to fraud or money laundering.
            </p>
          </Reveal>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {KYC_FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={i * 90} className="bg-[#070B14] p-10 hover:bg-[#0F1523] transition-colors duration-500">
                  <Icon size={26} strokeWidth={1.25} className="text-[#D4AF37]" />
                  <h3 className="mt-6 font-serif-display text-white text-2xl tracking-tight">{f.title}</h3>
                  <p className="mt-3 text-white/65 text-sm font-light leading-relaxed">{f.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company DD */}
      <section className="bg-[#0B1120] border-y border-white/5 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-7">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
              Companies & Investments
            </div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight max-w-xl">
              Due Diligence on <span className="italic">Companies</span> and Investments.
            </h2>
            <div className="mt-8 space-y-6 text-white/70 leading-relaxed font-light max-w-2xl">
              <p>
                Before investing in any company, we conduct extensive due diligence to assess
                financial health and operational stability. We thoroughly review financial
                background, governance structures, funding sources and regulatory framework.
              </p>
              <p>
                Our rigorous approach ensures that we make informed decisions and that each
                investment opportunity adheres to high standards of transparency and security.
              </p>
              <p className="text-[#D4AF37] text-sm uppercase tracking-[0.22em]">
                Every investment decision is backed by comprehensive analysis and risk assessment.
              </p>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-5">
            <div className="border border-white/10 bg-[#070B14] p-10">
              <div className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]">We Review</div>
              <ul className="mt-8 space-y-4">
                {COMPANY_REVIEW.map((c) => (
                  <li key={c} className="flex items-center gap-3 text-white/85">
                    <span className="w-6 h-6 inline-flex items-center justify-center border border-[#D4AF37]/40">
                      <Check size={12} className="text-[#D4AF37]" strokeWidth={2} />
                    </span>
                    <span className="font-light">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Property DD */}
      <section className="bg-[#070B14] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <Building2 size={28} strokeWidth={1.25} className="text-[#D4AF37]" />
            <div className="mt-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-white/50">
              <span className="text-[#D4AF37]">Legal Protection</span>
              <span className="w-8 h-px bg-white/20" />
              <span>Full Verification</span>
            </div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Property Due Diligence <br />
              <span className="italic">Before Purchase.</span>
            </h2>
            <div className="mt-8 space-y-6 text-white/70 leading-relaxed font-light max-w-xl">
              <p>
                Before purchasing any property, we perform a comprehensive due diligence to ensure
                the investment is sound and secure. This includes verifying property titles, a
                thorough legal and tax assessment, and examining any ongoing or historical disputes.
              </p>
              <p>
                Our expertise ensures that every real estate transaction is protected, reducing
                risk for our clients.
              </p>
            </div>

            <ul className="mt-10 space-y-4 max-w-md">
              {PROPERTY_CHECKS.map((c) => (
                <li key={c} className="flex items-center gap-3 text-white/85">
                  <span className="w-6 h-6 inline-flex items-center justify-center border border-[#D4AF37]/40">
                    <Check size={12} className="text-[#D4AF37]" strokeWidth={2} />
                  </span>
                  <span className="font-light">{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/8082233/pexels-photo-8082233.jpeg"
                alt="Luxury property due diligence"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#070B14]/70 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Transparency commitment */}
      <section className="bg-[#0B1120] border-y border-white/5 py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <Scale size={32} strokeWidth={1.25} className="text-[#D4AF37] mx-auto" />
          </Reveal>
          <Reveal delay={120}>
            <h2 className="mt-8 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Commitment to Transparency <br />
              <span className="italic">and Security.</span>
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-8 max-w-2xl mx-auto text-white/70 text-base sm:text-lg leading-relaxed font-light">
              Whether for investments in companies or real estate, at EMIVIA LIMITED we ensure
              that every decision is based on rigorous and transparent analyses. Our team is
              dedicated to safeguarding our clients&apos; interests and ensuring the success of each project.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Commission */}
      <section className="bg-[#070B14] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37] hairline">
              Commission and Transparency
            </div>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              A fair, transparent <span className="italic">structure.</span>
            </h2>
            <p className="mt-8 max-w-xl text-white/70 leading-relaxed font-light">
              For our services, we maintain a transparent commission structure, generally ranging
              between 4% and 12% depending on the nature of the investment. This commission
              reflects our commitment to delivering high-quality service while remaining
              competitive.
            </p>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-6">
            <div className="border border-white/10 bg-[#0B1120] p-12 sm:p-16 text-center">
              <div className="text-[11px] uppercase tracking-[0.32em] text-white/50">Commission Range</div>
              <div className="mt-6 flex items-end justify-center gap-6">
                <div className="text-7xl sm:text-8xl font-serif-display text-[#D4AF37] leading-none">4%</div>
                <div className="text-white/40 text-3xl pb-3">—</div>
                <div className="text-7xl sm:text-8xl font-serif-display text-[#D4AF37] leading-none">12%</div>
              </div>
              <div className="mt-10 inline-block text-[11px] uppercase tracking-[0.28em] text-white/50 border border-white/10 px-5 py-2">
                Depending on the nature of the investment
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection
        testId="due-diligence-cta"
        eyebrow="Compliance"
        title="Need a confidential assessment?"
        description="Our compliance and due diligence teams are ready to support your next investment decision."
      />
    </div>
  );
}
